<script>
import roundHelper from './mixins/roundHelper';
import MatchCard from '../components/DuelMatchCard.vue';
import Joiner from '@/components/tournaments/Joiner.vue';
import Grid from '../components/GridView';
import ToggleGrid from './ToggleGrid';
import MatchCardDetails from '../components/DuelMatchCardDetails.vue';

// UPPER GRID FUNCTIONS:
const getIncrementUpper = (roundIndex, roundsCount) => {
  /**
   *  r | di
   *  x | 2^f(x)
   *  ---------
   *  0 | 0
   *  ---------
   *  1 | 1
   *  ---------
   *  2 | 4
   *  3 | 4
   *  ---------
   *  4 | 8
   *  5 | 8
   *  ---------
   *  6 | 16
   *  7 | 16
   *  ---------
   *  ...
   *  ---------
   *  IF HAS GF:
   *  N      | 2^f(N) (set as `inc`)
   *  last-2 | inc
   *  last-1 | inc
   *  last   | inc
   *  ---------
   *  IF HAS NOT GF:
   *  N      | 2^f(N) (set as `inc`)
   *  last-1 | inc
   *  last   | inc
   *
   */
  // if tournament has GF - rounds count is odd ()
  const hasGrandFinal = roundsCount % 2;
  const forceStep = roundsCount < 6 ? 1 : 2; // if only 2 rounds, we has not double-step rounds and `2-in-1 joiners`

  // if is last round and is GF we remove 1 (1 step === 2 rounds) step for increment
  if (hasGrandFinal && roundIndex === roundsCount - 1) {
    roundIndex -= forceStep;
  }

  // remove 1 step
  if (roundIndex >= roundsCount - 2 - hasGrandFinal) {
    // isFinalRounds
    roundIndex -= forceStep;
  }

  return (
    2 **
    (roundIndex < 2 ? roundIndex : Math.floor((roundIndex + forceStep) / 2))
  );
};

const getRoundVisibleCountUpper = (roundIndex, pageSize, roundsCount) =>
  pageSize / getIncrementUpper(roundIndex, roundsCount);
const getRoundItemHeightUpper = (basicHeight, roundsCount) => roundIndex =>
  basicHeight * getIncrementUpper(roundIndex, roundsCount);
// get first match index in next round
const getMatchIndexUpper = (index, roundIndex) =>
  roundIndex < 2 || !(roundIndex % 2) ? Math.floor(index / 2) : index;
const getMatchCardUpper = roundsCount => roundIndex =>
  roundIndex && !(roundIndex % 2) && roundIndex < roundsCount - 1
    ? Joiner
    : MatchCard;

const matchPropsUpper = ({ roundIndex, roundsCount, matchIndex }) => {
  const hasGrandFinal = roundsCount % 2;
  const isFinalRounds = roundIndex >= roundsCount - 2 - hasGrandFinal;

  const toBind = roundIndex // not first round
    ? isFinalRounds // last 2 or 3 (if has GF) rounds
      ? hasGrandFinal && roundsCount - 2 === roundIndex // if has GF and penultimate round
        ? -2 // set wave-bind to up
        : 1 // Joiner penultimate final round
      : 0 // direct bundle with Joiner
    : matchIndex % 2
    ? -1 // bind to up
    : 1; // bind to down
  const fromBind = !(roundIndex % 2 || isFinalRounds)
    ? 3 // Joiner 2-in-1
    : hasGrandFinal && roundsCount - 1 === roundIndex // if GF
    ? 2 // set wave-bind to down
    : 0; // almost all are directly related to the previous rounds

  const offset = isFinalRounds
    ? {
        currentY:
          roundIndex >= roundsCount - 1 - hasGrandFinal
            ? `calc(50% - ${roundsCount < 8 ? 120 : 300}px)` // final & grand-final move to down (50% (centered) + 50% - 300px (cardHeight+spacing))
            : null,
      }
    : null;

  return {
    props: {
      isTreeMode: true,
      canSwapPlayers: true,
      toBind,
      fromBind,
      offset,
    },
  };
};

// LOWER GRID FUNCTIONS
const getIncrementLower = roundIndex => {
  /**
   *  r | di
   *  x | 2^f(x)
   *  ---------
   *  0 | ∞
   *  ---------
   *  1 | 2
   *  2 | 2
   *  ---------
   *  3 | 4
   *  4 | 4
   *  ---------
   *  5 | 8
   *  6 | 8
   *  ---------
   *  ...
   */
  return 2 ** (roundIndex ? Math.floor((roundIndex + 1) / 2) : Infinity);
};

const getRoundVisibleCountLower = (roundIndex, pageSize) =>
  pageSize / getIncrementLower(roundIndex);
const getRoundItemHeightLower = basicHeight => roundIndex =>
  (basicHeight * getIncrementLower(roundIndex)) / 2;
// get first match index in next round
const getMatchIndexLower = (index, roundIndex) =>
  roundIndex < 2 || roundIndex % 2 ? index : index / 2;
const matchPropsLower = ({ roundIndex, matchIndex, roundsCount }) => {
  const hasGrandFinal = roundsCount % 2;
  const isLast = roundIndex >= roundsCount - 2 - hasGrandFinal;

  return {
    props: {
      isTreeMode: true,
      canSwapPlayers: true,
      toBind: roundIndex % 2 ? +2 : matchIndex % 2 || isLast ? -1 : 1,
      fromBind: roundIndex % 2 ? 0 : -2,
    },
  };
};

let toggleGrid = null;

export default {
  name: 'TournamentDoubleElimination',
  functional: true,
  props: {
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  render(h, context) {
    return h('div', [
      h(Grid, {
        ...context.data,
        props: {
          roundName: roundHelper.methods.roundName,
          getRoundItemHeight: getRoundItemHeightUpper,
          getRoundVisibleCountBase: getRoundVisibleCountUpper,
          getNextMatchIndex: getMatchIndexUpper,
          matchProps: matchPropsUpper,
          isCollapsible: false,
          matchCardComponent: getMatchCardUpper,
          isAdmin: context.props.isAdmin,
          matchCardDetailsComponent: MatchCardDetails,
          grid: 'upper',
        },
        on: context.listeners,
      }),
      h(Grid, {
        ...context.data,
        props: {
          roundName: roundHelper.methods.roundName,
          getRoundItemHeight: getRoundItemHeightLower,
          getRoundVisibleCountBase: getRoundVisibleCountLower,
          getNextMatchIndex: getMatchIndexLower,
          matchProps: matchPropsLower,
          isCollapsible: false,
          matchCardComponent: MatchCard,
          matchCardDetailsComponent: MatchCardDetails,
          isAdmin: context.props.isAdmin,
          grid: 'lower',
        },
        on: {
          fetched() {
            if (toggleGrid) {
              setTimeout(toggleGrid.checkGridHeight, 600); // debounceTime = 500ms
            }
          },
        },
      }),
      h(ToggleGrid, {
        on: {
          created(t) {
            toggleGrid = t;
          },
        },
      }),
    ]);
  },
};
</script>
